// apps/lk/src/app/page.tsx
'use client';
import Link from 'next/link';
import { useAuth } from '@/lib/auth';
import { useDashboard } from '@/lib/use-dashboard';
import { ArchetypeHero } from '@/components/ArchetypeHero';
import { InsightsBlock } from '@/components/InsightsBlock';

export default function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const { data, loading, error } = useDashboard(user?.id);

  if (authLoading || loading) return <DashboardSkeleton />;
  if (!user) return null;
  if (error && !data) return (
    <div className="mx-auto max-w-md p-12 text-center">
      <p className="text-sm text-slate-500">{error}</p>
    </div>
  );

  const { bigFive, archetype, meta } = data;
  const done = meta?.testsCompleted ?? 0;
  const total = meta?.testsTotal ?? 4;
  const pending = total - done;

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6 lg:p-8">
      {/* ════ ПРИВЕТСТВИЕ ════ */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Здравствуйте, {user.firstName || user.displayName || 'приятель'}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {done === 0
            ? 'Пройдите первый тест, чтобы узнать свой архетип'
            : done === total
              ? 'Все тесты пройдены — ниже ваш полный профиль'
              : `${pending} ${pending === 1 ? 'тест остался' : 'теста осталось'} до полного профиля`}
        </p>
      </div>

      {/* ════ АРХЕТИП ════ */}
      <ArchetypeHero
        archetype={archetype}
        archetypeCode={bigFive?.archetypeCode}
        jungCode={bigFive?.jungCode}
        domains={bigFive?.domains}
      />

      {/* ════ ИНСАЙТЫ ════ */}
      {bigFive?.derived && <InsightsBlock dashboard={data} />}

      {/* ════ CTA: недостающие тесты ════ */}
      {pending > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-base font-semibold text-slate-900">
                Профиль заполнен на {Math.round((done / total) * 100)}%
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Остальные тесты добавят деталей к вашему профилю
              </p>
            </div>
            <Link
              href="/tests"
              className="shrink-0 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700"
            >
              Пройти тесты
            </Link>
          </div>
        </div>
      )}

      {/* ════ БЫСТРЫЕ ДЕЙСТВИЯ ════ */}
      <div className="grid gap-3 sm:grid-cols-2">
        <QuickLink href="/my-profile" title="Мой профиль" desc="Полная картина" />
        <QuickLink href="/results" title="Результаты" desc="Детально по тестам" />
      </div>
    </div>
  );
}

function QuickLink({ href, title, desc }: { href: string; title: string; desc: string }) {
  return (
    <Link
      href={href}
      className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-300 transition group-hover:text-slate-500">
        <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </Link>
  );
}

function DashboardSkeleton() {
  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6 lg:p-8 animate-pulse">
      <div className="space-y-2">
        <div className="h-7 w-64 rounded bg-slate-200" />
        <div className="h-4 w-80 rounded bg-slate-100" />
      </div>
      <div className="h-56 rounded-2xl bg-slate-100" />
      <div className="space-y-3">
        <div className="h-4 w-32 rounded bg-slate-200" />
        <div className="h-20 rounded-xl bg-slate-100" />
        <div className="h-20 rounded-xl bg-slate-100" />
      </div>
    </div>
  );
}
